import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import SentimentSatisfiedAltRoundedIcon from "@mui/icons-material/SentimentSatisfiedAltRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import { useAuth } from "../auth/AuthContext";
import { supportApi } from "../services/api";
import { subscribeSupport } from "../utils/supportBus";
import { openAuth } from "../utils/authBus";
import { openEventStream } from "../utils/supportStream";
import ChatImage from "./ChatImage";
import UserAvatar from "./UserAvatar";

const QUICK = [
  "Çekim talebim ne zaman yatar?",
  "Nasıl yatırım yaparım?",
  "Kripto gönderdim, bakiyem gelmedi",
  "Bonus çevirimim ne durumda?",
];

const EMOJIS = ["👍", "🙏", "😊", "🔥", "😅", "🎉", "❤️", "😢"];

const clock = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : "";

const dayStamp = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  const today = new Date().toDateString() === date.toDateString();
  return today
    ? `Bugün ${clock(iso)}`
    : date.toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const minuteOf = (iso) => Math.floor(new Date(iso || Date.now()).getTime() / 60000);
const isLive = (item) => item && item.kind !== "ticket";

const SupportChat = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [thread, setThread] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [askClose, setAskClose] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const logRef = useRef(null);
  const fileRef = useRef(null);
  const typingRef = useRef(null);
  const seenRef = useRef(0);
  const syncedRef = useRef(false);

  const applyLive = useCallback((next) => {
    if (next && !isLive(next)) return;
    const active = next && ["open", "waiting", "in_progress"].includes(next.status) ? next : null;
    setThread(active);
    if (!active) setAgentTyping(false);
  }, []);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("marsilya-open-support", show);
    return () => window.removeEventListener("marsilya-open-support", show);
  }, []);

  useEffect(() => subscribeSupport((detail) => {
    if (detail.action === "open") setOpen(true);
  }), []);

  useEffect(() => {
    document.body.classList.toggle("cx-open", open);
    return () => document.body.classList.remove("cx-open");
  }, [open]);

  useEffect(() => {
    if (!user) {
      setThread(null);
      return;
    }
    supportApi
      .thread()
      .then((data) => applyLive(data.live || data.thread))
      .catch(() => {});
  }, [user, applyLive]);

  useEffect(() => {
    if (!user) return undefined;
    return openEventStream("/api/support/stream", (event) => {
      if (event.type === "typing" && event.from === "admin") {
        setAgentTyping(Boolean(event.text));
        return;
      }
      if (event.live) applyLive(event.live);
      else if (event.thread && isLive(event.thread)) applyLive(event.thread);
    });
  }, [user, applyLive]);

  const messages = useMemo(() => thread?.messages || [], [thread]);

  useEffect(() => {
    const incoming = messages.filter((item) => item.from === "admin").length;
    if (open || !syncedRef.current) {
      // Messages already on screen before the widget opened are not "new".
      syncedRef.current = true;
      seenRef.current = incoming;
      setUnseen(0);
      return;
    }
    setUnseen(Math.max(0, incoming - seenRef.current));
  }, [messages, open]);

  useEffect(() => {
    if (!logRef.current) return;
    logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, agentTyping, open, emojiOpen]);

  const bumpTyping = (value) => {
    setText(value);
    if (!user || !thread) return;
    clearTimeout(typingRef.current);
    typingRef.current = setTimeout(() => supportApi.typing(value).catch(() => {}), 200);
  };

  const send = async (payload) => {
    if (!user) {
      setError("Destek için giriş yap");
      return;
    }
    if (!String(payload.text || "").trim() && !payload.image) return;
    setBusy(true);
    try {
      let current = thread;
      if (!current) {
        const started = await supportApi.live();
        current = started.live || started.thread;
        applyLive(current);
      }
      const data = await supportApi.send({ ...payload, kind: "live", threadId: current?.id });
      applyLive(data.thread || data.live);
      setText("");
      setError("");
      setEmojiOpen(false);
      supportApi.typing("").catch(() => {});
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onFile = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      setError("Sadece JPG veya PNG gönderebilirsin");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setError("Dosya 4 MB sınırını aşıyor");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => send({ text: text.trim(), image: reader.result });
    reader.readAsDataURL(file);
  };

  const endChat = async () => {
    setBusy(true);
    try {
      await supportApi.close("live");
      applyLive(null);
      setAskClose(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const agentName = thread?.assignedName || "Marsilya Destek";
  const agentAvatar = thread?.agentAvatar || "";
  const statusText = !user
    ? "Giriş yapman gerekiyor"
    : thread?.assignedName
      ? thread.agentOnline
        ? `${thread.assignedName} · çevrimiçi`
        : `${thread.assignedName} sohbete bağlandı`
      : "Genelde birkaç dakika içinde yanıtlıyoruz";

  return (
    <>
      <div className={`cx-widget${open ? " is-open" : ""}`}>
        <section className="cx-panel" role="dialog" aria-label="Canlı destek">
          <header className="cx-head">
            <div className="cx-head-top">
              <div className="cx-avatars">
                <UserAvatar className="cx-avatar cx-avatar-img" src={agentAvatar} name={agentName} />
                <UserAvatar className="cx-avatar alt cx-avatar-img" name="Marsilya" />
                <em className={`cx-dot${thread?.agentOnline ? " on" : ""}`} aria-hidden />
              </div>
              <div className="cx-head-actions">
                {thread ? (
                  <button type="button" onClick={() => setAskClose(true)} aria-label="Sohbeti bitir">
                    <PowerSettingsNewRoundedIcon fontSize="small" />
                  </button>
                ) : null}
                <button type="button" onClick={() => setOpen(false)} aria-label="Kapat">
                  <CloseRoundedIcon fontSize="small" />
                </button>
              </div>
            </div>
            <h3>Merhaba{user ? ` ${user.username}` : ""} 👋</h3>
            <p>{statusText}</p>
          </header>

          {!user ? (
            <div className="cx-guest">
              <b>Canlı destek 7/24 açık</b>
              <p>Sohbeti başlatmak için hesabına giriş yap. Yatırım, çekim ve bonus sorularında hemen yanıt veriyoruz.</p>
              <button
                type="button"
                className="cx-guest-go"
                onClick={() => {
                  setOpen(false);
                  openAuth("login");
                }}
              >
                Giriş yap
              </button>
            </div>
          ) : (
            <>
              <div className="cx-log" ref={logRef}>
                <div className="cx-row in">
                  <UserAvatar className="cx-bub-ava" src={agentAvatar} name={agentName} />
                  <div className="cx-bubble">
                    <span>Merhaba! Marsilya destek ekibine hoş geldin. Sorununu yaz, hemen bakıyoruz.</span>
                    <em>şimdi</em>
                  </div>
                </div>

                {messages.map((item, index) => {
                  const prev = messages[index - 1];
                  const gap = !prev || Math.abs(minuteOf(item.at) - minuteOf(prev.at)) >= 5;
                  const incoming = item.from === "admin";
                  return (
                    <React.Fragment key={`${item.at}-${index}`}>
                      {gap ? <div className="cx-stamp">{dayStamp(item.at)}</div> : null}
                      <div className={`cx-row ${incoming ? "in" : "out"}`}>
                        {incoming ? (
                          <UserAvatar className="cx-bub-ava" src={item.byAvatar || agentAvatar} name={item.byName || agentName} />
                        ) : null}
                        <div className="cx-bubble">
                          {item.text ? <span>{item.text}</span> : null}
                          {item.image ? <ChatImage name={item.image} /> : null}
                          <em>
                            {clock(item.at)}
                            {incoming ? null : <DoneAllRoundedIcon sx={{ fontSize: 13, ml: 0.4 }} />}
                          </em>
                        </div>
                        {!incoming ? (
                          <UserAvatar className="cx-bub-ava is-me" src={user?.avatar} name={user?.username} />
                        ) : null}
                      </div>
                    </React.Fragment>
                  );
                })}

                {agentTyping ? (
                  <div className="cx-row in">
                    <UserAvatar className="cx-bub-ava" src={agentAvatar} name={agentName} />
                    <div className="cx-bubble cx-typing">
                      <i />
                      <i />
                      <i />
                    </div>
                  </div>
                ) : null}

                {messages.length < 2 ? (
                  <div className="cx-quick">
                    {QUICK.map((item) => (
                      <button key={item} type="button" onClick={() => send({ text: item })}>
                        {item}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              {error ? <p className="cx-error">{error}</p> : null}

              {emojiOpen ? (
                <div className="cx-emoji">
                  {EMOJIS.map((item) => (
                    <button key={item} type="button" onClick={() => setText((value) => `${value}${item}`)}>
                      {item}
                    </button>
                  ))}
                </div>
              ) : null}

              <form
                className="cx-bar"
                onSubmit={(event) => {
                  event.preventDefault();
                  send({ text });
                }}
              >
                <button type="button" onClick={() => setEmojiOpen((value) => !value)} aria-label="Emoji">
                  <SentimentSatisfiedAltRoundedIcon fontSize="small" />
                </button>
                <input
                  value={text}
                  onChange={(event) => bumpTyping(event.target.value)}
                  placeholder="Mesajını yaz…"
                  maxLength={1000}
                />
                <button type="button" onClick={() => fileRef.current?.click()} aria-label="Dosya ekle">
                  <AttachFileRoundedIcon fontSize="small" />
                </button>
                <button type="submit" className="cx-send" disabled={busy || !text.trim()} aria-label="Gönder">
                  <SendRoundedIcon fontSize="small" />
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  hidden
                  onChange={(event) => onFile(event.target.files?.[0])}
                />
              </form>
            </>
          )}

          {askClose ? (
            <div className="cx-ask">
              <div className="cx-ask-card">
                <b>Sohbeti bitirelim mi?</b>
                <p>Sohbet kapanır, yeni mesajda tekrar temsilci bağlanır.</p>
                <div className="cx-ask-row">
                  <button type="button" onClick={() => setAskClose(false)}>
                    Vazgeç
                  </button>
                  <button type="button" className="is-yes" disabled={busy} onClick={endChat}>
                    Bitir
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </section>

        <button
          type="button"
          className="cx-launcher"
          onClick={() => setOpen((value) => !value)}
          aria-label={open ? "Desteği kapat" : "Canlı destek"}
        >
          {open ? <CloseRoundedIcon /> : <ChatRoundedIcon />}
          {!open && unseen > 0 ? <em>{unseen > 9 ? "9+" : unseen}</em> : null}
        </button>
      </div>
    </>
  );
};

export default SupportChat;
